import { Component } from '@theme/component';

const FILTER_SECTION_ID = 'section-rendering-collection-filters';

/**
 * @typedef {Object} Filter
 * @property {string} label The filter label
 * @property {string} param_name The parameter name
 * @property {string} type The filter type
 * @property {FilterValue[]} values The filter values
 */

/**
 * @typedef {Object} FilterValue
 * @property {string} label The filter value label
 * @property {string} value The filter value
 * @property {number} count The filter value count
 */

/**
 * Search Launcher Component
 * Handles search functionality with filters from the search launcher section.
 * Dynamically fetches collection filter values via the Section Rendering API.
 * Supports cascading filters: selecting a value in one dropdown disables unavailable
 * options in other dropdowns based on the filtered product set.
 */
class SearchLauncherComponent extends Component {
  /** @type {Map<string, Filter[]>} Cache keyed by collectionHandle|filterParams */
  #filterCache = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /**
   * Bound pageshow handler — registered on window in connectedCallback and removed in
   * disconnectedCallback so bfcache restores (browser back button) reset the loading state.
   * @type {(event: PageTransitionEvent) => void}
   */
  #onPageShow = (event) => {
    if (/** @type {PageTransitionEvent} */ (event).persisted) {
      this.setLoadingState(false);
    }
  };

  /**
   * Monotonically increasing counter used to discard stale fetch results.
   * Incremented on every fetch attempt (cache hit or network request).
   * @type {number}
   */
  #latestRequestId = 0;

  constructor() {
    super();
    this.defaultCollection = this.getAttribute('data-collection-default') || null;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pageshow', this.#onPageShow);
    const initialCollection = this.#getActiveCollection();
    if (initialCollection) {
      this.#fetchAndPopulate(initialCollection);
    } else {
      // No collection pre-selected — disable filter dropdowns until the user picks one.
      const dropdowns = this.querySelectorAll('[data-filter-type="dropdown"]');
      this.#setDropdownLoadingState(dropdowns, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Get the currently active collection handle from the picker or default setting
   * @returns {string} The collection handle
   */
  #getActiveCollection() {
    const picker = this.querySelector('[data-filter-type="collection"]');
    // When a rich/native collection picker is present, return its raw value (may be '').
    // Only fall back to defaultCollection if there is no picker in the DOM at all.
    return picker ? /** @type {HTMLInputElement} */ (picker).value : this.defaultCollection || '';
  }

  /**
   * Collect currently selected values from all filter dropdowns.
   * @returns {Record<string, string>} Map of filterParam → selected value
   */
  #getActiveFilters() {
    /** @type {Record<string, string>} */
    const filters = {};
    for (const dropdown of this.querySelectorAll('[data-filter-type="dropdown"]')) {
      const select = /** @type {HTMLSelectElement} */ (dropdown);
      const param = select.dataset.filterParam;
      if (select.value && param) {
        filters[param] = select.value;
      }
    }
    return filters;
  }

  /**
   * Build a stable cache key from collection handle and active filter params.
   * Params are sorted alphabetically so key is order-independent.
   * @param {string} collectionHandle
   * @param {Record<string, string>} [activeFilters]
   * @returns {string}
   */
  #buildCacheKey(collectionHandle, activeFilters = {}) {
    const params = new URLSearchParams(
      Object.entries(activeFilters)
        .filter(([, v]) => Boolean(v))
        .sort(([a], [b]) => a.localeCompare(b))
    );
    const paramStr = params.toString();
    return paramStr ? `${collectionHandle}|${paramStr}` : collectionHandle;
  }

  /**
   * Handle collection picker change.
   * Resets all filter dropdowns and fetches values for the new collection.
   * @param {Event} event
   */
  handleCollectionChange(event) {
    const collectionHandle = /** @type {HTMLInputElement} */ (event.target).value;
    if (!collectionHandle) {
      // Placeholder / "Select an option" was chosen — clear and lock all filter dropdowns.
      const dropdowns = this.querySelectorAll('[data-filter-type="dropdown"]');
      for (const dropdown of dropdowns) {
        const select = /** @type {HTMLSelectElement} */ (dropdown);
        while (select.options.length > 1) select.remove(1);
        const noOption = document.createElement('option');
        noOption.value = '';
        noOption.disabled = true;
        noOption.textContent = 'No options available';
        select.add(noOption);
        select.disabled = true;
      }
      return;
    }
    this.#fetchAndPopulate(collectionHandle);
  }

  /**
   * Handle a filter dropdown change.
   * Re-fetches available options with all current selections applied and disables
   * options that have no matching products in the other dropdowns (cascade update).
   * @param {Event} event
   * @returns {Promise<void>}
   */
  async handleFilterChange(event) {
    const changedSelect = /** @type {HTMLSelectElement} */ (event.target);
    const collectionHandle = this.#getActiveCollection();
    if (!collectionHandle) return;
    const activeFilters = this.#getActiveFilters();
    await this.#fetchAndApplyAvailability(collectionHandle, activeFilters, changedSelect);
  }

  /**
   * Fetch filters for a collection (no active filters) and fully repopulate all dropdowns.
   * Shows a loading state while fetching.
   * @param {string} collectionHandle
   */
  async #fetchAndPopulate(collectionHandle) {
    const dropdowns = this.querySelectorAll('[data-filter-type="dropdown"]');
    if (dropdowns.length === 0) return;

    this.#setDropdownLoadingState(dropdowns, true);

    const filters = await this.#fetchFilters(collectionHandle);
    if (!filters) return; // aborted or stale

    this.#populateDropdowns(dropdowns, filters);
  }

  /**
   * Fetch filters with active filter params and apply availability to other dropdowns.
   * Runs silently without a loading state — dropdowns remain interactive during the fetch.
   * If clearing a stale selection changes the active filter set, performs one settle pass.
   * @param {string} collectionHandle
   * @param {Record<string, string>} activeFilters
   * @param {HTMLSelectElement} changedSelect - The dropdown that triggered the change
   */
  async #fetchAndApplyAvailability(collectionHandle, activeFilters, changedSelect) {
    const filters = await this.#fetchFilters(collectionHandle, activeFilters);
    if (!filters) return;

    const needsSettle = this.#applyFilterAvailability(filters, changedSelect);

    if (needsSettle) {
      // One settle pass: re-fetch with the updated (cleared) filter selections.
      const settledFilters = this.#getActiveFilters();
      const settledData = await this.#fetchFilters(collectionHandle, settledFilters);
      if (settledData) {
        this.#applyFilterAvailability(settledData, changedSelect);
      }
    }
  }

  /**
   * Core fetch method. Fetches filter data from the Section Rendering API,
   * with optional active filter params applied (for cascading).
   * Results are cached per collection+filters combination.
   * Returns null if the request was aborted or superseded by a newer one.
   * @param {string} collectionHandle
   * @param {Record<string, string>} [activeFilters]
   * @returns {Promise<Filter[] | null>}
   */
  async #fetchFilters(collectionHandle, activeFilters = {}) {
    const requestId = ++this.#latestRequestId;
    const cacheKey = this.#buildCacheKey(collectionHandle, activeFilters);

    if (this.#filterCache.has(cacheKey)) {
      // Discard if a newer request has already been initiated
      if (requestId !== this.#latestRequestId) return null;
      return this.#filterCache.get(cacheKey) || [];
    }

    // Abort any in-flight request
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    try {
      const filterParams = new URLSearchParams(
        Object.entries(activeFilters).filter(([, v]) => Boolean(v))
      );
      const queryStr = filterParams.toString();
      const url = `/collections/${collectionHandle}?section_id=${FILTER_SECTION_ID}${queryStr ? `&${queryStr}` : ''}`;

      const response = await fetch(url, { signal: this.#abortController.signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch filters: ${response.status}`);
      }

      const html = await response.text();

      // Discard if superseded
      if (requestId !== this.#latestRequestId) return null;

      const filters = this.#parseFiltersFromHTML(html);
      this.#filterCache.set(cacheKey, filters);
      return filters;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.error('[SearchLauncher] Error fetching collection filters:', error);
      return null;
    }
  }

  /**
   * Parse filter JSON from the section rendering response HTML
   * @param {string} html - The raw HTML response
   * @returns {Filter[]} Parsed filter objects
   */
  #parseFiltersFromHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('[data-collection-filters]');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch {
      console.error('[SearchLauncher] Failed to parse filter JSON');
      return [];
    }
  }

  /**
   * Fully repopulate filter dropdowns with fetched filter data.
   * Used on collection change — replaces all options and re-enables the selects.
   * @param {NodeListOf<Element>} dropdowns - The filter dropdown elements
   * @param {Filter[]} filters - The filter data from the API
   */
  #populateDropdowns(dropdowns, filters) {
    for (const dropdown of dropdowns) {
      const select = /** @type {HTMLSelectElement} */ (dropdown);
      const filterParam = select.dataset.filterParam;
      const filter = filters.find((f) => f.param_name === filterParam);

      // Preserve the first option (placeholder)
      const placeholder = select.options[0];
      const previousValue = select.value;

      // Clear all options after placeholder
      while (select.options.length > 1) {
        select.remove(1);
      }

      if (filter && filter.values.length > 0) {
        const showCount = select.dataset.showValueCount === 'true';

        for (const value of filter.values) {
          const option = document.createElement('option');
          option.value = value.value;
          option.textContent = showCount && value.count ? `${value.label} (${value.count})` : value.label;
          select.add(option);
        }

        // Restore previous selection if it still exists
        if (previousValue && Array.from(select.options).some((o) => o.value === previousValue)) {
          select.value = previousValue;
        }
      } else {
        const noOptions = document.createElement('option');
        noOptions.value = '';
        noOptions.disabled = true;
        noOptions.textContent = 'No options available';
        select.add(noOptions);
      }

      select.disabled = false;

      if (placeholder) {
        placeholder.disabled = false;
      }
    }
  }

  /**
   * Soft-update filter dropdowns based on active filter selections (cascade update).
   * Enables or disables individual options based on their count in the filtered response.
   * Does NOT add or remove options — only changes their disabled state and label counts.
   * The changed dropdown is still updated but its current selection is never auto-disabled.
   *
   * @param {Filter[]} filters - Filter data returned for the current collection + active filters
   * @param {HTMLSelectElement} changedSelect - The dropdown whose change triggered this update
   * @returns {boolean} True if any other dropdown had its selection cleared (triggers a settle pass)
   */
  #applyFilterAvailability(filters, changedSelect) {
    const dropdowns = this.querySelectorAll('[data-filter-type="dropdown"]');
    let needsSettle = false;

    for (const dropdown of dropdowns) {
      const select = /** @type {HTMLSelectElement} */ (dropdown);
      const filterParam = select.dataset.filterParam;
      const filter = filters.find((f) => f.param_name === filterParam);

      if (!filter) continue;

      const isChangedDropdown = select === changedSelect;
      const showCount = select.dataset.showValueCount === 'true';
      const valueMap = new Map(filter.values.map((v) => [v.value, v]));
      const previousValue = select.value;

      for (const option of select.options) {
        if (!option.value) continue; // skip placeholder

        const filterValue = valueMap.get(option.value);
        const count = filterValue ? filterValue.count : 0;

        // Update count labels when enabled
        if (showCount && filterValue) {
          option.textContent = count ? `${filterValue.label} (${count})` : filterValue.label;
        }

        // Always keep the user's just-selected value enabled so they can see/change it.
        // All other zero-count options are disabled.
        const forceEnabled = isChangedDropdown && option.value === previousValue;
        option.disabled = !forceEnabled && count === 0;
      }

      // For non-changed dropdowns: if the current selection is now unavailable, clear it.
      if (!isChangedDropdown && previousValue) {
        const filterValue = valueMap.get(previousValue);
        if (!filterValue || filterValue.count === 0) {
          select.value = '';
          needsSettle = true;
        }
      }
    }

    return needsSettle;
  }

  /**
   * Set loading/disabled state on filter dropdowns
   * @param {NodeListOf<Element>} dropdowns - The filter dropdown elements
   * @param {boolean} isLoading - Whether dropdowns should show loading state
   */
  #setDropdownLoadingState(dropdowns, isLoading) {
    for (const dropdown of dropdowns) {
      /** @type {HTMLSelectElement} */ (dropdown).disabled = isLoading;
    }
  }

  /**
   * Handle search button click.
   * Collects all filter values and navigates to search/collection page.
   */
  handleSearch() {
    // Show loading state
    this.setLoadingState(true);

    // Get collection value
    const collectionValue = this.#getActiveCollection();

    // Get text search value
    const textInput = this.querySelector('[data-filter-type="text"]');
    const searchQuery = textInput ? /** @type {HTMLInputElement} */ (textInput).value.trim() : '';

    // Get all filter dropdowns
    const filterDropdowns = this.querySelectorAll('[data-filter-type="dropdown"]');
    const filterParams = new URLSearchParams();

    // Add filter values (not search query yet)
    filterDropdowns.forEach((dropdown) => {
      const filterParam = dropdown.getAttribute('data-filter-param');
      const filterValue = /** @type {HTMLSelectElement} */ (dropdown).value;

      if (filterValue && filterParam) {
        filterParams.set(filterParam, filterValue);
      }
    });

    // Add search query if present
    if (searchQuery) {
      filterParams.set('q', searchQuery);
      filterParams.set('type', 'product');
    }

    // Build the target URL
    let targetUrl;

    if (searchQuery) {
      // If there's a search query, go to search page
      targetUrl = `/search?${filterParams.toString()}`;
    } else if (collectionValue && collectionValue !== 'all') {
      // If a specific collection is selected, go to that collection with filters
      targetUrl = `/collections/${collectionValue}?view=default`;
      if (filterParams.size > 0) {
        targetUrl += `&${filterParams.toString()}`;
      }
    } else {
      // Default to all products collection with filters
      targetUrl = '/collections/all';
      if (filterParams.size > 0) {
        targetUrl += `?${filterParams.toString()}`;
      }
    }

    // Navigate to the target URL
    window.location.href = targetUrl;
  }

  /**
   * Set loading state on the search button
   * @param {boolean} isLoading - Whether to show loading state
   */
  setLoadingState(isLoading) {
    const { searchButton, loadingSpinner } = this.refs;

    if (searchButton) {
      /** @type {HTMLButtonElement} */ (searchButton).disabled = isLoading;

      if (loadingSpinner) {
        /** @type {HTMLElement} */ (loadingSpinner).style.display = isLoading ? 'inline-block' : 'none';
      }
    }
  }
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.search-launcher__filter-toggle');
  if (!toggle) return;
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  const panelId = toggle.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : null;
  if (panel) {
    panel.classList.toggle('is-open', !expanded);
    // Move focus to first interactive element in the panel when opening
    if (!expanded) {
      const firstInput = panel.querySelector('select, input, button');
      if (firstInput) {
        // Short delay so the CSS transition has started and the element is no longer clipped
        setTimeout(() => /** @type {HTMLElement} */ (firstInput).focus(), 50);
      }
    }
  }
});

customElements.define('search-launcher-component', SearchLauncherComponent);
